import { useEffect, useRef } from 'react';
import { useGetMaybeViewer } from '../hooks/useGetMaybeViewer';
import { consumePendingAuthEvent } from './analytics';

/**
 * Headless component that emits pending analytics auth events once the
 * viewer query resolves and a `sandId` is available. Sits high in the
 * tree (under ApolloProvider + CognitoProvider) so it observes every
 * auth transition for the lifetime of the app.
 *
 * See `setPendingAuthEvent` in `./analytics` for the other half of
 * the contract.
 */
export const AnalyticsTracker = () => {
  const { viewer } = useGetMaybeViewer();
  const hasFiredForSandId = useRef<string | null>(null);

  useEffect(() => {
    const sandId = viewer?.sandId;
    if (!sandId) return;
    // Guard against React StrictMode double-mount and any render churn:
    // fire at most once per sandId in the lifetime of this tab.
    if (hasFiredForSandId.current === sandId) return;

    const fired = consumePendingAuthEvent(sandId);
    if (fired) {
      hasFiredForSandId.current = sandId;
    }
  }, [viewer?.sandId]);

  return null;
};
